<template>
	<ion-item
		v-if="!page.children?.length"
		button
		:color="isActive ? '' : 'branding'"
		lines="none"
		:detail="false"
		class="hydrated font-bold"
		:class="{ active: isActive }"
		@click="onNavigate(page.url)"
	>
		<ion-icon
			v-if="page.iosIcon"
			slot="start"
			aria-hidden="true"
			:ios="page.iosIcon"
		></ion-icon>
		<div class="flex flex-row items-center gap-2">
			<div>
				{{ page.title }}
			</div>
			<div v-if="page.tag" class="w-min rounded-md bg-branding-400 px-1">
				{{ page.tag }}
			</div>
		</div>
	</ion-item>

	<ion-accordion-group
		v-else
		:multiple="false"
		:value="hasActiveChild ? page.title : ''"
	>
		<ion-accordion :value="page.title" :disabled="!visibleChildren.length">
			<ion-item slot="header" color="branding" class="font-bold">
				<ion-icon
					v-if="page.iosIcon"
					slot="start"
					aria-hidden="true"
					:ios="page.iosIcon"
				></ion-icon>
				<ion-label>{{ page.title }}</ion-label>
			</ion-item>

			<div slot="content">
				<ion-item
					v-for="(child, childIndex) in visibleChildren"
					:key="childIndex"
					button
					:color="isChildActive(child) ? '' : 'branding'"
					lines="none"
					:detail="false"
					class="hydrated submenu-item font-bold"
					:class="{ active: isChildActive(child) }"
					@click="onNavigate(child.url)"
				>
					<ion-icon
						v-if="child.iosIcon"
						slot="start"
						aria-hidden="true"
						:ios="child.iosIcon"
					></ion-icon>
					<div class="flex flex-row items-center gap-2">
						<div>
							{{ child.title }}
						</div>
						<div
							v-if="child.tag"
							class="w-min rounded-md bg-branding-400 px-1"
						>
							{{ child.tag }}
						</div>
					</div>
				</ion-item>
			</div>
		</ion-accordion>
	</ion-accordion-group>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import {
		IonIcon,
		IonItem,
		IonLabel,
		IonAccordion,
		IonAccordionGroup,
		useIonRouter,
	} from "@ionic/vue";
	import { MenuItem } from "@/views/SideMenu/SideMenu.vue";
	import { useRoute } from "vue-router";

	const props = defineProps<{
		page: MenuItem;
	}>();

	const emit = defineEmits<(e: "menuItemClicked") => void>();

	const ionRouter = useIonRouter();
	const route = useRoute();

	// Check if this menu item's URL is active
	function isUrlActive(
		menuItemUrl: string | undefined,
		currentUrl: string,
	): boolean {
		if (!menuItemUrl) return false;
		return currentUrl.startsWith(menuItemUrl.toLowerCase());
	}

	const isActive = computed(() => {
		return isUrlActive(props.page.url, route.path);
	});

	const hasActiveChild = computed(() => {
		if (!visibleChildren.value.length) return false;
		return visibleChildren.value.some((child) =>
			isUrlActive(child.url, route.path),
		);
	});

	// Check if a specific child is active
	const isChildActive = (
		child: NonNullable<MenuItem["children"]>[number],
	) => {
		return isUrlActive(child.url, route.path);
	};

	// Filter out hidden children
	const visibleChildren = computed(() => {
		return props.page.children?.filter((child) => !child.hidden) || [];
	});

	const onNavigate = (url: string | undefined) => {
		if (!url) return;
		ionRouter.push(url);
		emit("menuItemClicked");
	};
</script>

<style scoped>
	ion-item.active {
		--background: var(--ion-color-branding-tint);
		--color: var(--ion-color-branding-contrast);
	}

	ion-item.disabled {
		pointer-events: none;
	}
</style>
